import {getCurrentInstance, onUnmounted, ref, watch} from "vue";
import {usePreferredReducedMotion} from "@vueuse/core";

/** Delay between two steps of the writing. */
const TICK_MS = 32;

/** Characters written per step when nothing is late. */
const MIN_STEP = 6;

/**
 * Steps over which a backlog is spread: a long answer arrives whole and is
 * written faster than a short one, so both finish in about the same time.
 */
const CATCH_UP_STEPS = 60;

/** A line of a table: it opens with a pipe. */
const TABLE_LINE = /^\s*\|/;

/** The row of dashes that turns a paragraph of pipes into a grid. */
const TABLE_SEPARATOR = /^\s*\|[\s:|-]+$/;

/**
 * The first index at or after `at` where a line holds no unclosed construct.
 *
 * Scanned rather than counted: a run of three asterisks — a rule, or a bold
 * italic — and a `**` sitting inside a code span both defeat a count of
 * pairs, and invert it for everything that follows.
 *
 * Emphasis with underscores is left out on purpose: `_` also lives inside
 * words, and reading it as a mark would hold a cut on an identifier.
 */
const settled = (line: string, at: number): number => {
    let index = 0;
    let code = false;
    let bold = false;
    let emphasis = false;
    let link = false;

    while (index < line.length) {
        if (index >= at && !(code || bold || emphasis || link)) {
            return index;
        }

        if (line[index] === '`') {
            code = !code;
            index++;
        } else if (code) {
            index++;
        } else if (line.startsWith('**', index)) {
            bold = !bold;
            index += 2;
        } else if (line[index] === '*') {
            emphasis = !emphasis;
            index++;
        } else if (line[index] === '[') {
            link = true;
            index++;
        } else if (line[index] === ')' && link) {
            link = false;
            index++;
        } else {
            index++;
        }
    }

    return line.length;
};

/**
 * The end of a table row, carrying the separator row along with a header.
 *
 * A table is read as a grid only once its separator has arrived: on its own,
 * a header renders as a paragraph of pipes and then turns into a grid under
 * the reader.
 */
const withSeparator = (text: string, at: number): number => {
    const next = text.indexOf('\n', at + 1);
    const end = next === -1 ? text.length : next;

    return TABLE_SEPARATOR.test(text.slice(at + 1, end)) ? end : at;
};

/**
 * Where to cut a text so what shows reads as finished.
 *
 * The word is completed, so no half-word ever flashes. Then the line decides:
 * a construct left open renders as its own punctuation — « [le planning »
 * before it becomes a link, two asterisks before they become bold — and the
 * cut waits for it to close. The line bounds that search because every inline
 * construct closes on the line that opened it, which is also what keeps a
 * lone « *** » from being read as an emphasis that never ends.
 *
 * A table is cut at the end of a row instead, its shape being the line and
 * not the word.
 */
export const boundary = (text: string, at: number): number => {
    if (at >= text.length) {
        return text.length;
    }

    let end = at;

    while (end < text.length && !/\s/.test(text[end])) {
        end++;
    }

    if (end >= text.length) {
        return text.length;
    }

    const start = text.lastIndexOf('\n', end - 1) + 1;
    const next = text.indexOf('\n', end);
    const stop = next === -1 ? text.length : next;
    const line = text.slice(start, stop);

    if (TABLE_LINE.test(line)) {
        return withSeparator(text, stop);
    }

    return start + settled(line, end - start);
};

/**
 * Show a text as though it were being written, while it may still grow.
 *
 * The source is the truth and is never touched: what comes back is a view that
 * catches up with it, word by word. The answer of the assistant arrives whole
 * — the server validates a turn before its first byte — and this is what
 * gives it back the pace of something being said.
 *
 * @param source The text to show, read again at every step.
 * @param typing Whether this text is being received now. False shows it whole
 *               from the start, which is how an answer already read comes
 *               back.
 */
const useTypedText = (source: () => string, typing: boolean) => {
    const displayed = ref<string>('');
    const reducedMotion = usePreferredReducedMotion();

    let paced = typing;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const stop = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    };

    const show = () => {
        stop();
        displayed.value = source();
    };

    /**
     * Give up the pace: the whole text, now, and for the rest of its life.
     */
    const reveal = () => {
        paced = false;
        show();
    };

    const step = () => {
        timer = null;

        const target = source();

        // The view is a prefix of the source, or it starts over: a text that
        // changed under it would otherwise be shown spliced.
        if (!target.startsWith(displayed.value)) {
            displayed.value = '';
        }

        const behind = target.length - displayed.value.length;

        if (behind <= 0) {
            return;
        }

        const advance = Math.max(MIN_STEP, Math.ceil(behind / CATCH_UP_STEPS));

        displayed.value = target.slice(0, boundary(target, displayed.value.length + advance));

        if (displayed.value.length < target.length) {
            timer = setTimeout(step, TICK_MS);
        }
    };

    const schedule = () => {
        if (!timer) {
            timer = setTimeout(step, TICK_MS);
        }
    };

    // Someone who asked for less motion reads the answer whole and still.
    watch(
        source,
        () => paced && reducedMotion.value !== 'reduce' ? schedule() : show(),
        {immediate: true}
    );

    if (getCurrentInstance()) {
        onUnmounted(stop);
    }

    return {displayed, reveal};
};

export default useTypedText;
